import { NgClass } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, inject, Injector, input } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { FEHLER_OPTIONS } from '../../../../shared/constants/order-document-labels';
import { SelectOnFocusDirective } from '../../../../shared/directives/select-on-focus.directive';
import { IOrderDocumentFalsePosition, IOrderDocumentPosition } from '../../../../shared/models/order-document.model';

// Validators.required lets a whitespace-only value through, but the false-positions API trims and
// rejects it — catch it here so the row is marked red instead of the whole save failing.
function notBlank(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return value === null || value === undefined || String(value).trim() === '' ? { required: true } : null;
}

// Builds one Fehlererfassung row. Exported because the page builds the rows of a loaded document
// itself (see CreateOrderDocumentComponent.patchFormValues). Rows are built disabled for roles
// that may not manage them, so a stored row they can't edit never makes their form invalid.
export function createFalsePositionGroup(
  fb: FormBuilder,
  falsePosition: Partial<IOrderDocumentFalsePosition> | undefined,
  disabled: boolean
): FormGroup {
  return fb.group({
    _id: [falsePosition?._id ?? null],
    posNr: [{ value: falsePosition?.posNr ?? '', disabled }, notBlank],
    date: [{ value: falsePosition?.date ? new Date(falsePosition.date) : new Date(), disabled }, Validators.required],
    employeeNumber: [{ value: falsePosition?.employeeNumber ?? '', disabled }, notBlank],
    inspectorNumber: [{ value: falsePosition?.inspectorNumber ?? '', disabled }, notBlank],
    code: [{ value: falsePosition?.code ?? null, disabled }, Validators.required],
    quantity: [{ value: falsePosition?.quantity ?? null, disabled }, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]],
    remark: [{ value: falsePosition?.remark ?? '', disabled }],
  });
}

// The Fehlererfassung (false positions) table of an Auftragsbegleitschein. Every role reads it; only
// ADMIN and ENDKONTROLLE get `editable` (add/edit/delete). Edits the page's `falsePositions` FormArray
// in place — loading, pruning and saving the rows stays with the page.
@Component({
  selector: 'app-false-positions-table',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, DatePickerModule, SelectModule, SelectOnFocusDirective],
  templateUrl: './false-positions-table.component.html',
  styleUrls: ['./false-positions-table.component.scss'],
  // Not OnPush on purpose — see PositionsTableComponent: the rows' touched/error state is also
  // changed by the page (markAllAsTouched() on a failed Speichern), which OnPush would miss.
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FalsePositionsTableComponent {
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);

  public readonly falsePositions = input.required<FormArray>();
  // The document exists (edit mode). Before that there's nothing to show or record yet.
  public readonly saved = input(false);
  // A Kunde is assigned. Fehler can only be recorded for a document with a Kunde.
  public readonly hasCustomer = input(false);
  // Rows can be added, removed and edited — ADMIN/ENDKONTROLLE on a saved document with a Kunde. Otherwise the
  // rows are read-only: their controls are disabled by the page, and the add/remove buttons hidden.
  public readonly editable = input(false);
  public readonly isSubmitting = input(false);

  public readonly fehlerOptions = FEHLER_OPTIONS;

  // Typed for [formGroup] under strictTemplates (FormArray.controls is AbstractControl[]).
  public get rows(): FormGroup[] {
    return this.falsePositions().controls as FormGroup[];
  }

  // "Fehler hinzufügen": an empty row the user fills in completely.
  public addRow(): void {
    this.pushRow({}, 'posNr');
  }

  // "Fehler erfassen" on a Zeiterfassung row (called by the page, which also switches to this tab):
  // copies the row's Pos. and Verursacher into a new, independent row (no link back to the position
  // is kept), so only the Prüfer, the Fehler code and the Stück are left to fill in. Stück is not
  // copied: the faulty count is usually not the Zeiterfassung's Stück. Datum is not copied either: a
  // Fehler is dated the day it's recorded, i.e. today (createFalsePositionGroup's default).
  public addRowFromPosition(position: IOrderDocumentPosition): void {
    this.pushRow(
      {
        // Pos. is a number input in the Zeiterfassung, but the API expects a string
        posNr: String(position.posNr ?? ''),
        employeeNumber: position.employeeNumber ?? '',
      },
      // The first field left to fill in
      'inspectorNumber'
    );
  }

  // Duplicate icon next to Bemerkung: inserts a copy right below the row, e.g. for a second Fehler
  // code at the same Pos. Everything is copied — Datum included — except the _id (it's a new entry).
  // Focus lands on the Fehler code, the likeliest change.
  public duplicateRow(index: number): void {
    const source = this.falsePositions().at(index)?.getRawValue() as IOrderDocumentFalsePosition | undefined;
    if (!source) return;
    const copy = this.insertRow(
      {
        posNr: source.posNr,
        date: source.date,
        employeeNumber: source.employeeNumber,
        inspectorNumber: source.inspectorNumber,
        code: source.code,
        quantity: source.quantity,
        remark: source.remark,
      },
      'code',
      index + 1
    );
    // A duplicate is a deliberate, complete entry: mark it dirty so the page's pruneUntouchedNewRows()
    // saves it even if nothing in it gets changed (unlike an untouched "Fehler hinzufügen" row).
    copy?.markAsDirty();
  }

  public removeRow(index: number): void {
    if (!this.editable()) return;
    this.falsePositions().removeAt(index);
    this.falsePositions().markAsDirty();
  }

  public isFieldInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && control.touched;
  }

  private pushRow(values: Partial<IOrderDocumentFalsePosition>, focusField: keyof IOrderDocumentFalsePosition): void {
    this.insertRow(values, focusField, this.falsePositions().length);
  }

  private insertRow(values: Partial<IOrderDocumentFalsePosition>, focusField: keyof IOrderDocumentFalsePosition, index: number): FormGroup | null {
    if (!this.editable()) return null;
    const group = createFalsePositionGroup(this.fb, values, false);
    this.falsePositions().insert(index, group);
    // insert() alone doesn't mark the form dirty — without this, unsavedChangesGuard wouldn't warn.
    this.falsePositions().markAsDirty();

    // The new row doesn't exist in the DOM yet, and coming from "Fehler erfassen" the tab is still
    // hidden until the page's tab switch has rendered.
    afterNextRender(
      () => {
        const row = (this.elementRef.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.false-position-row')[index];
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const field = row?.querySelector<HTMLElement>(`[formcontrolname="${focusField}"]`);
        // A p-select host isn't focusable itself — its combobox element inside is.
        const target = field?.matches('input') ? field : field?.querySelector<HTMLElement>('[role="combobox"]');
        target?.focus({ preventScroll: true });
      },
      { injector: this.injector }
    );
    return group;
  }
}
